// SPDX-License-Identifier: MIT
import { receiveFonts } from './fonts.ts';
/**
 * Player frame (FR-13): runs inside a sandboxed, cross-origin iframe with no network access. The
 * host sends a bundle (reel, brand, compiled custom scenes) and optional audio over postMessage;
 * this page plays it in real time with the same engine the server renders with.
 *
 * Clock: the AudioContext when sound is playing, otherwise performance.now(). Frames are drawn on
 * requestAnimationFrame at the clock's time, so picture and sound never drift apart.
 */
import * as engine from '@pulsereel/engine';
import { type Runtime, createRuntime, resolveBrand, ENGINE_VERSION } from '@pulsereel/engine';
import {
  type Brand,
  HostMessageSchema,
  PLAYER_PROTOCOL,
  PLAYER_PROTOCOL_VERSION,
  type PlayerAudio,
  type PlayerErrorCode,
  type PlayerMessage,
  type PlayerQuality,
  type Reel,
  ReelSchema,
  applyJsonPatch,
} from '@pulsereel/schema/browser';

declare global {
  var __pulsereelEngine: unknown;
  var __pulsereelScenes: Record<string, unknown> | undefined;
}

// Custom scene bundles import '@pulsereel/engine' from this global instead of carrying a copy.
globalThis.__pulsereelEngine = engine;

const BASE_W = 1280;
const BASE_H = 720;
/** Internal resolution steps (fraction of 1280×720) used when frames drop. */
const SCALES = [1, 0.75, 0.5];

type Out = PlayerMessage extends infer M
  ? M extends { source: unknown; v: unknown }
    ? Omit<M, 'source' | 'v'>
    : never
  : never;

function send(msg: Out, transfer: Transferable[] = []): void {
  window.parent.postMessage({ source: PLAYER_PROTOCOL, v: PLAYER_PROTOCOL_VERSION, ...msg }, '*', transfer);
}

class PlayerError extends Error {
  readonly code: PlayerErrorCode;
  constructor(code: PlayerErrorCode, message: string) {
    super(message);
    this.code = code;
  }
}

const stage = document.getElementById('stage') as HTMLDivElement;
const watermarkEl = document.getElementById('watermark') as HTMLDivElement;
const soundButton = document.getElementById('sound') as HTMLButtonElement;
// The frame has no page language of its own: the browser's is the reader's best guess.
soundButton.querySelector('span')!.textContent = navigator.language.toLowerCase().startsWith('zh')
  ? '点按开启声音'
  : 'Tap for sound';

// --- State -------------------------------------------------------------------------------------

let reel: Reel | null = null;
let brand: Brand | null = null;
let runtime: Runtime | null = null;
let canvas: HTMLCanvasElement | null = null;
let quality: PlayerQuality = 'draft';
let scaleStep = 0;
/** Draw every frame (1) or every other frame (2, i.e. 30 fps on a 60 Hz display). */
let frameDivisor = 1;

let playing = false;
let muted = false;
let position = 0; // seconds, while paused
let clockOrigin = 0; // performance-clock seconds at which t = 0, while playing without sound
let loopRange: { a: number; b: number } | null | false = false;

let audioCtx: AudioContext | null = null;
let audioBuffer: AudioBuffer | null = null;
let audioSource: AudioBufferSourceNode | null = null;
let gain: GainNode | null = null;
let audioOrigin = 0; // AudioContext seconds at which t = 0, while sound plays
let audioState: 'none' | 'ready' | 'blocked' = 'none';
let audioGeneration = 0;
let sourceGeneration = 0;
let failed = false;

const now = () => performance.now() / 1000;
const duration = () => runtime?.duration ?? 0;
const soundOn = () => audioSource !== null && audioCtx?.state === 'running';

function currentTime(): number {
  if (!playing) return position;
  return soundOn() ? audioCtx!.currentTime - audioOrigin : now() - clockOrigin;
}

function loopBounds(): { a: number; b: number } | null {
  if (loopRange === false) return null;
  const d = duration();
  if (loopRange === null) return { a: 0, b: d };
  return { a: Math.min(loopRange.a, d), b: Math.min(Math.max(loopRange.b, loopRange.a + 0.05), d) };
}

function sendState(): void {
  send({ type: 'state', playing, muted, audio: audioState });
}

// --- Rendering ----------------------------------------------------------------------------------

function mount(): void {
  if (!reel || !brand) return;
  const scale = SCALES[scaleStep]!;
  const next = document.createElement('canvas');
  // A fresh canvas per (re)mount: its WebGL context and resources go with the old one.
  const rt = createRuntime(reel, brand, {
    canvas: next,
    width: Math.round(BASE_W * scale),
    height: Math.round(BASE_H * scale),
    fps: reel.fps,
    subframes: quality === 'high' ? 2 : 1,
    quality: quality === 'high' ? 'full' : 'draft',
    scenes: (globalThis.__pulsereelScenes ?? {}) as Record<string, engine.SceneModule<never>>,
  });
  canvas?.remove();
  stage.prepend(next);
  canvas = next;
  runtime = rt;
}

function draw(t: number): void {
  if (!runtime || failed) return;
  try {
    if (quality === 'high') runtime.renderFrame(Math.round(t * reel!.fps));
    else runtime.renderTime(t);
  } catch (err) {
    failed = true;
    pause();
    send({ type: 'error', code: 'scene_failed', message: (err as Error).message });
  }
}

function beatAt(t: number): number {
  return reel ? t / (60 / reel.bpm) : 0;
}

function sceneIndexAt(beat: number): number {
  if (!reel) return -1;
  const b = Math.min(beat, reel.beats - 1e-6);
  return reel.scenes.findIndex((s) => b >= s.beats[0] && b < s.beats[1]);
}

let lastSent = -1;
function report(t: number): void {
  if (t === lastSent) return;
  lastSent = t;
  const beat = beatAt(t);
  send({ type: 'timeupdate', t, beat, sceneIndex: sceneIndexAt(beat) });
}

// Performance: frames shown per second over a 2 s window; degrade when it stays low.
let perfWindowStart = now();
let perfFrames = 0;
let perfDropped = 0;
let lastTick = 0;
let tickCount = 0;

function measure(ts: number): void {
  const interval = lastTick ? ts - lastTick : 0;
  lastTick = ts;
  perfFrames++;
  // An interval over 1.5× the display's ~16.7 ms means at least one frame was skipped.
  if (interval > 25) perfDropped += Math.max(1, Math.round(interval / 16.7) - 1);
  const elapsed = now() - perfWindowStart;
  if (elapsed < 2) return;
  const fps = perfFrames / (elapsed * frameDivisor);
  send({
    type: 'perf',
    fps: Math.round(fps * 10) / 10,
    droppedFrames: perfDropped,
    scale: SCALES[scaleStep]!,
  });
  if (playing && fps * frameDivisor < 50) {
    if (scaleStep < SCALES.length - 1) {
      scaleStep++;
      mount();
    } else if (frameDivisor === 1) frameDivisor = 2;
  }
  perfWindowStart = now();
  perfFrames = 0;
  perfDropped = 0;
}

function tick(ts: number): void {
  requestAnimationFrame(tick);
  if (!runtime || !playing) return;
  tickCount++;
  if (tickCount % frameDivisor !== 0) return;
  measure(ts);
  let t = currentTime();
  const bounds = loopBounds();
  if (bounds && t >= bounds.b) {
    seekTo(bounds.a, true);
    t = bounds.a;
  } else if (t >= duration()) {
    position = duration() - 1e-6;
    playing = false;
    stopSource();
    draw(position);
    report(position);
    send({ type: 'ended' });
    sendState();
    return;
  }
  draw(t);
  report(t);
}
requestAnimationFrame(tick);

// --- Audio --------------------------------------------------------------------------------------

function ensureContext(): AudioContext {
  if (!audioCtx) {
    audioCtx = new AudioContext({ latencyHint: 'interactive' });
    gain = audioCtx.createGain();
    gain.connect(audioCtx.destination);
    gain.gain.value = 0;
  }
  return audioCtx;
}

async function setAudio(audio: PlayerAudio | null | undefined): Promise<void> {
  const generation = ++audioGeneration;
  const t = currentTime();
  stopSource();
  clockOrigin = now() - t;
  audioBuffer = null;
  audioState = 'none';
  if (audio) {
    const ctx = ensureContext();
    let decoded: AudioBuffer;
    try {
      if (audio.kind === 'pcm') {
        const frames = audio.channels[0]!.length;
        decoded = ctx.createBuffer(2, frames, audio.sampleRate);
        decoded.copyToChannel(audio.channels[0]!, 0);
        decoded.copyToChannel(audio.channels[1] ?? audio.channels[0]!, 1);
      } else decoded = await ctx.decodeAudioData(audio.data);
    } catch (err) {
      if (generation !== audioGeneration) return;
      throw new PlayerError('audio_failed', (err as Error).message);
    }
    if (generation !== audioGeneration) return;
    audioBuffer = decoded;
    audioState = 'ready';
  }
  if (playing) await startSource(currentTime());
}

function stopSource(): void {
  sourceGeneration++;
  if (gain && audioCtx) {
    gain.gain.cancelScheduledValues(audioCtx.currentTime);
    gain.gain.setValueAtTime(0, audioCtx.currentTime);
  }
  if (!audioSource) return;
  audioSource.onended = null;
  try {
    audioSource.stop();
  } catch {
    // Already stopped.
  }
  audioSource.disconnect();
  audioSource = null;
}

/** Start sound at reel time `t`. If the browser blocks it, playback continues silently. */
async function startSource(t: number, fade = true): Promise<void> {
  stopSource();
  const generation = sourceGeneration;
  if (!audioBuffer || muted || !playing) return;
  const ctx = ensureContext();
  if (ctx.state !== 'running') {
    // resume() can stay pending forever without a user gesture; don't wait on it.
    await Promise.race([ctx.resume().catch(() => undefined), new Promise((r) => setTimeout(r, 150))]);
  }
  if (generation !== sourceGeneration || muted || !playing) return;
  if (ctx.state !== 'running') {
    audioState = 'blocked';
    updateSoundButton();
    clockOrigin = now() - t;
    sendState();
    return;
  }
  audioState = 'ready';
  updateSoundButton();
  const src = ctx.createBufferSource();
  src.buffer = audioBuffer;
  src.connect(gain!);
  const lead = 0.03; // schedule slightly ahead so the first samples are not clipped
  const startsAt = ctx.currentTime + lead;
  gain!.gain.setValueAtTime(fade ? 0 : 1, startsAt);
  if (fade) gain!.gain.linearRampToValueAtTime(1, startsAt + 0.8);
  audioOrigin = ctx.currentTime + lead - t;
  src.start(startsAt, Math.max(0, t));
  audioSource = src;
  send({ type: 'soundstart', delayMs: Math.max(0, (startsAt - ctx.currentTime) * 1000) });
}

/** "Tap for sound" only when sound is wanted (not muted) but the browser blocked it. */
function updateSoundButton(): void {
  soundButton.hidden = !(audioState === 'blocked' && !muted);
}

soundButton.addEventListener('click', () => {
  // A gesture inside the frame: now the context may start.
  const t = currentTime();
  void ensureContext()
    .resume()
    .then(async () => {
      if (playing) await startSource(t);
      else audioState = 'ready';
      updateSoundButton();
      sendState();
    });
});

// --- Transport ----------------------------------------------------------------------------------

async function play(): Promise<void> {
  if (!runtime) throw new PlayerError('not_loaded', 'load a bundle first');
  if (playing) return;
  failed = false;
  let t = position;
  if (t >= duration() - 1e-3) t = loopBounds()?.a ?? 0;
  playing = true;
  clockOrigin = now() - t;
  perfWindowStart = now();
  perfFrames = 0;
  perfDropped = 0;
  lastTick = 0;
  await startSource(t);
  sendState();
}

function pause(): void {
  if (!playing) return;
  position = currentTime();
  playing = false;
  stopSource();
  // The host's time otherwise stays at the last frame drawn, which can lag the clock when the
  // frame is slow to draw.
  report(position);
  sendState();
}

function seekTo(t: number, keepPlaying = playing): void {
  const clamped = Math.min(Math.max(t, 0), Math.max(0, duration() - 1e-6));
  position = clamped;
  if (keepPlaying && playing) {
    clockOrigin = now() - clamped;
    // A pending resume has no source yet: supersede it at the new position too.
    if (audioBuffer) void startSource(clamped, !audioSource);
  } else {
    draw(clamped);
    report(clamped);
  }
}

// --- Loading and editing -------------------------------------------------------------------------

function loadScript(source: string): Promise<void> {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(new Blob([source], { type: 'text/javascript' }));
    const el = document.createElement('script');
    el.src = url;
    el.onload = () => {
      URL.revokeObjectURL(url);
      resolve();
    };
    el.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new PlayerError('scene_failed', 'a custom scene failed to load'));
    };
    document.head.appendChild(el);
  });
}

const CJK = /[぀-ヿ㐀-鿿가-힯豈-﫿＀-￯]/;

async function loadFonts(): Promise<void> {
  if (!runtime || !brand) return;
  const text = runtime.glyphs + (brand.name ?? '');
  const fonts = resolveBrand(brand).fonts;
  const families = [fonts.display, fonts.mono, ...(CJK.test(text) ? [fonts.cjk] : [])];
  const sample = Array.from(new Set(Array.from(`${text}Aa0`))).join('');
  // A missing font falls back to the system face rather than failing the whole film.
  await Promise.all(families.map((f) => document.fonts.load(`700 64px "${f}"`, sample).catch(() => [])));
}

function info(): void {
  send({
    type: 'ready',
    duration: duration(),
    fps: reel!.fps,
    bpm: reel!.bpm,
    beats: reel!.beats,
    scenes: reel!.scenes.map((s) => ({ id: s.id, type: s.type, beats: s.beats })),
  });
}

function setWatermark(on: boolean): void {
  watermarkEl.hidden = !on;
}

/** Swap in a new reel/brand, re-validating; the old one stays if this throws. */
async function swap(nextReel: Reel, nextBrand: Brand): Promise<void> {
  if (!runtime) throw new PlayerError('not_loaded', 'load a bundle first');
  const parsed = ReelSchema.parse(nextReel);
  runtime.setReel(parsed, nextBrand);
  reel = parsed;
  brand = nextBrand;
  failed = false;
  await loadFonts();
  if (position > duration()) position = 0;
  if (!playing) draw(position);
  info();
}

type HostMsg = ReturnType<typeof HostMessageSchema.parse>;

async function handle(msg: HostMsg): Promise<void> {
  switch (msg.type) {
    case 'load': {
      pause();
      // Invalidate a decode belonging to the previous film before loading scripts or fonts.
      await setAudio(null);
      for (const source of Object.values(msg.bundle.scenes)) await loadScript(source);
      reel = msg.bundle.reel;
      brand = msg.bundle.brand;
      quality = msg.quality;
      scaleStep = 0;
      frameDivisor = 1;
      failed = false;
      try {
        mount();
      } catch (err) {
        const message = (err as Error).message;
        throw new PlayerError(/WebGL2/.test(message) ? 'webgl_unavailable' : 'invalid_bundle', message);
      }
      loopRange = msg.loop ? null : false;
      await receiveFonts(msg.fonts, true);
      await loadFonts();
      setWatermark(msg.watermark);
      await setAudio(msg.audio);
      position = 0;
      draw(0);
      report(0);
      info();
      if (msg.autoplay) await play();
      sendState();
      return;
    }
    case 'play':
      return play();
    case 'pause':
      return pause();
    case 'seek':
      return seekTo(msg.t);
    case 'setLoop':
      loopRange = msg.range;
      return;
    case 'applyPatch': {
      if (!reel || !brand) throw new PlayerError('not_loaded', 'load a bundle first');
      let next: Reel;
      try {
        next = applyJsonPatch(reel, msg.jsonPatch);
        if (msg.fonts) await receiveFonts(msg.fonts);
        await swap(next, brand);
      } catch (err) {
        if (err instanceof PlayerError) throw err;
        throw new PlayerError('invalid_patch', (err as Error).message);
      }
      return;
    }
    case 'setBrand':
      if (!reel) throw new PlayerError('not_loaded', 'load a bundle first');
      try {
        if (msg.fonts) await receiveFonts(msg.fonts);
        await swap(reel, msg.brand);
      } catch (err) {
        if (err instanceof PlayerError) throw err;
        throw new PlayerError('invalid_bundle', (err as Error).message);
      }
      return;
    case 'setAudio':
      await setAudio(msg.audio);
      sendState();
      return;
    case 'getSoundEvents':
      if (!runtime) throw new PlayerError('not_loaded', 'load a bundle first');
      send({ type: 'soundEvents', id: msg.id ?? 0, events: runtime.soundEvents() });
      return;
    case 'setMuted': {
      const t = currentTime();
      muted = msg.muted;
      if (muted) {
        stopSource();
        clockOrigin = now() - t;
      }
      // Unmuting while the browser held sound back: try again (works if the host page had a gesture).
      if (!muted && playing && audioBuffer && !soundOn()) await startSource(t);
      updateSoundButton();
      sendState();
      return;
    }
  }
}

// Loads and transport commands retain order. Audio decoding acknowledges when complete, but must
// not block pause/play: a host may deliberately fall back to silent playback after a deadline.
let queue = Promise.resolve();
window.addEventListener('message', (event) => {
  if (event.source !== window.parent) return;
  const parsed = HostMessageSchema.safeParse(event.data);
  if (!parsed.success) {
    const data = event.data as { source?: unknown; id?: unknown };
    if (data?.source === PLAYER_PROTOCOL)
      send({
        type: 'error',
        ...(typeof data.id === 'number' ? { id: data.id } : {}),
        code: 'bad_message',
        message: parsed.error.issues.map((i) => `${i.path.join('.')}: ${i.message}`).join('; '),
      });
    return;
  }
  const msg = parsed.data;
  if (msg.type === 'setMuted' && !msg.muted) {
    // Do this on receipt, before a queued load/decode can consume the visitor's activation.
    // No audioSession override: Safari's Silent Mode behavior remains the browser's decision.
    try {
      void ensureContext()
        .resume()
        .catch(() => undefined);
    } catch {
      // A browser without Web Audio can still show the film silently.
    }
  }
  const respond = async () => {
    try {
      await handle(msg);
      if (msg.id !== undefined) send({ type: 'ack', id: msg.id });
    } catch (err) {
      const code = err instanceof PlayerError ? err.code : 'invalid_bundle';
      send({
        type: 'error',
        ...(msg.id !== undefined ? { id: msg.id } : {}),
        code,
        message: (err as Error).message,
      });
    }
  };
  queue = queue.then(() => {
    if (msg.type === 'setAudio') void respond();
    else return respond();
  });
});

send({ type: 'hello', engine: ENGINE_VERSION });
